<template>
  <div>
    <BaseSnackbarUI
      v-model="uSnackbar.show"
      :text="uSnackbar.text"
      :color="uSnackbar.color"
      :timeout="uSnackbar.timeout"
      :bottom="false"
      :top="true"
      :right="false"
    />

    <v-card>
      <v-card-title class="headline d-flex align-center">
        <span>Delivery Notes</span>

        <v-spacer />

        <v-btn
          text
          class="mr-2"
          @click="resetFilters"
        >
          <v-icon left>
            mdi-filter-remove-outline
          </v-icon>
          Clear Filters
        </v-btn>

        <v-btn
          color="primary"
          :loading="loading"
          @click="fetchDeliveryNotes"
        >
          <v-icon left>
            mdi-refresh
          </v-icon>
          Refresh
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="search"
              label="Search by number, status, partner, sales order, address, contact or note"
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-magnify"
              @input="handleSearchInput"
              @click:clear="clearSearch"
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="filters.status"
              :items="statusItems"
              label="Status"
              outlined
              dense
              clearable
              @change="applyFilters"
            />
          </v-col>

          <v-col cols="12" md="4" class="d-flex align-start justify-end">
            <v-btn
              color="secondary"
              outlined
              @click="toggleFilters"
            >
              <v-icon left>
                mdi-filter-variant
              </v-icon>
              Advanced Filters
              <v-chip
                v-if="activeFiltersCount"
                small
                color="primary"
                text-color="white"
                class="ml-2"
              >
                {{ activeFiltersCount }}
              </v-chip>
            </v-btn>
          </v-col>
        </v-row>

        <v-expansion-panels
          v-model="filterPanel"
          class="mb-4"
          flat
        >
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div class="d-flex align-center">
                <v-icon left>
                  mdi-tune
                </v-icon>
                <span>Advanced Filters</span>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <div
                v-for="section in filterSections"
                :key="section.key"
              >
                <v-divider
                  v-if="section.divider"
                  class="my-4"
                />
                        
                <div class="subtitle-2 mb-2">
                  {{ section.title }}
                </div>
            
                <v-row dense>
                  <v-col
                    v-for="field in section.fields"
                    :key="field.key"
                    cols="12"
                    :md="field.md || 3"
                  >
                    <v-select
                      v-if="field.type === 'select'"
                      v-model="filters[field.key]"
                      :items="field.items || []"
                      :label="field.label"
                      outlined
                      dense
                      clearable
                    />
                
                    <v-text-field
                      v-else
                      v-model="filters[field.key]"
                      :label="field.label"
                      :type="field.type || 'text'"
                      :suffix="field.suffix"
                      outlined
                      dense
                      clearable
                      @keyup.enter="applyFilters"
                    />
                  </v-col>
                </v-row>
              </div>
          
              <div class="d-flex justify-end mt-4">
                <v-btn
                  text
                  class="mr-2"
                  @click="resetFilters"
                >
                  Clear Filters
                </v-btn>
            
                <v-btn
                  color="primary"
                  :loading="loading"
                  @click="applyFilters"
                >
                  Apply Filters
                </v-btn>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-data-table
          :headers="headers"
          :items="deliveryNotes"
          :loading="loading"
          :options.sync="options"
          :server-items-length="total"
          :footer-props="footerProps"
          class="elevation-1"
        >
          <template v-slot:item.number="{ item }">
            <strong>{{ item.number || '#'+item.id || '-' }}</strong>
          </template>

          <template v-slot:item.sales_order_id="{ item }">
            <span>#{{ item.sales_order_id }}</span>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip
              small
              dark
              :color="resolveStatusColor(item.status)"
            >
              {{ formatStatus(item.status) }}
            </v-chip>
          </template>

          <template v-slot:item.document_date="{ item }">
            {{ formatDate(item.document_date) }}
          </template>

          <template v-slot:item.delivery_date="{ item }">
            {{ formatDate(item.delivery_date) }}
          </template>

          <template v-slot:item.issued_at="{ item }">
            {{ formatDateTime(item.issued_at) }}
          </template>

          <template v-slot:item.created_at="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>

          <template v-slot:item.shipping_address="{ item }">
            <div>
              <div>{{ formatShippingAddress(item) }}</div>
              <small v-if="item.contact_name">
                Contact: {{ item.contact_name }}
              </small>
            </div>
          </template>

          <template v-slot:item.total_weight="{ item }">
            {{ formatWeight(item.total_weight) }}
          </template>

          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @click="openDetails(item)"
                >
                  <v-icon>
                    mdi-eye
                  </v-icon>
                </v-btn>
              </template>
              <span>Open details</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  :disabled="!item.pdf_path"
                  v-bind="attrs"
                  v-on="on"
                  @click="openPdf(item)"
                >
                  <v-icon>
                    mdi-file-pdf-box
                  </v-icon>
                </v-btn>
              </template>
              <span>Open PDF</span>
            </v-tooltip>
          </template>

          <template v-slot:no-data>
            <div class="pa-6 text-center">
              No delivery notes found.
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <delivery-note-planner-dialog
      ref="deliveryNoteDialog"
      v-model="deliveryNoteDialog"
      @saved="fetchDeliveryNotes"
      @issued="fetchDeliveryNotes"
      @closed="fetchDeliveryNotes"
    />
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import BaseSnackbarUI from "../../../components/BaseSnackbarUI.vue";
import DeliveryNotePlannerDialog from "./DeliveryNotePlannerDialog.vue";

const ENDPOINT = "transport/delivery-note/list";

const DEFAULT_FILTERS = () => ({
  id: "",
  id_min: "",
  id_max: "",
  number: "",
  status: "",
  sales_order_id: "",

  partner_name: "",
  partner_tax_number: "",

  billing_name: "",
  billing_country: "",
  billing_zip: "",
  billing_city: "",
  billing_address: "",

  shipping_name: "",
  shipping_country: "",
  shipping_zip: "",
  shipping_city: "",
  shipping_address: "",

  contact_name: "",
  contact_phone: "",
  contact_email: "",

  document_date_from: "",
  document_date_to: "",
  delivery_date_from: "",
  delivery_date_to: "",
  issued_at_from: "",
  issued_at_to: "",
  cancelled_at_from: "",
  cancelled_at_to: "",
  pdf_generated_at_from: "",
  pdf_generated_at_to: "",
  created_at_from: "",
  created_at_to: "",
  updated_at_from: "",
  updated_at_to: "",

  total_weight_min: "",
  total_weight_max: "",
  items_count_min: "",
  items_count_max: "",

  locale: "",
  currency_code: "",
  has_pdf: "",

  comment: "",
  internal_note: "",

  created_by: "",
  issued_by: "",
  cancelled_by: "",
});

export default {
  name: "DeliveryNotesIndex",

  components: {
    BaseSnackbarUI,
    DeliveryNotePlannerDialog,
  },

  data() {
    return {
      loading: false,
      search: "",
      searchTimeout: null,
      filterPanel: null,
      deliveryNoteDialog: false,

      deliveryNotes: [],
      total: 0,

      options: {
        page: 1,
        itemsPerPage: 15,
        sortBy: ["created_at"],
        sortDesc: [true],
      },

      footerProps: {
        "items-per-page-options": [15, 25, 50, 100],
      },

      filters: DEFAULT_FILTERS(),

      statusItems: [
        { text: "Draft", value: "draft" },
        { text: "Planned", value: "planned" },
        { text: "Issued", value: "issued" },
        { text: "Cancelled", value: "cancelled" },
      ],

      hasPdfItems: [
        { text: "With PDF", value: "1" },
        { text: "Without PDF", value: "0" },
      ],

      headers: [
        {
          text: "Number",
          value: "number",
        },
        {
          text: "Status",
          value: "status",
        },
        {
          text: "Sales Order",
          value: "sales_order_id",
        },
        {
          text: "Partner",
          value: "partner_name",
        },
        {
          text: "Shipping Address",
          value: "shipping_address",
          sortable: false,
        },
        {
          text: "Document Date",
          value: "document_date",
        },
        {
          text: "Delivery Date",
          value: "delivery_date",
        },
        {
          text: "Issued At",
          value: "issued_at",
        },
        {
          text: "Created At",
          value: "created_at",
        },
        {
          text: "Weight",
          value: "total_weight",
        },
        {
          text: "Rows",
          value: "items_count",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          align: "end",
        },
      ],

      uSnackbar: {
        show: false,
        text: "",
        color: "success",
        timeout: 3000,
      },
    };
  },

  computed: {
    activeFiltersCount() {
      return Object.values(this.filters).filter((value) => {
        return value !== null && value !== undefined && String(value).trim() !== "";
      }).length;
    },

    filterSections() {
      return [
        {
          key: "basic",
          title: "Basic Data",
          divider: false,
          fields: [
            { key: "id", label: "Delivery note ID", type: "number" },
            { key: "id_min", label: "Minimum ID", type: "number" },
            { key: "id_max", label: "Maximum ID", type: "number" },
            { key: "number", label: "Delivery note number" },
            { key: "status", label: "Status", type: "select", items: this.statusItems },
            { key: "sales_order_id", label: "Sales order ID", type: "number" },
          ],
        },
        {
          key: "partner",
          title: "Partner",
          divider: true,
          fields: [
            { key: "partner_name", label: "Partner name", md: 6 },
            { key: "partner_tax_number", label: "Partner tax number", md: 6 },
          ],
        },
        {
          key: "billing",
          title: "Billing Address",
          divider: true,
          fields: [
            { key: "billing_name", label: "Billing name" },
            { key: "billing_country", label: "Billing country" },
            { key: "billing_zip", label: "Billing ZIP" },
            { key: "billing_city", label: "Billing city" },
            { key: "billing_address", label: "Billing address", md: 6 },
          ],
        },
        {
          key: "shipping",
          title: "Shipping Address",
          divider: true,
          fields: [
            { key: "shipping_name", label: "Shipping name" },
            { key: "shipping_country", label: "Shipping country" },
            { key: "shipping_zip", label: "Shipping ZIP" },
            { key: "shipping_city", label: "Shipping city" },
            { key: "shipping_address", label: "Shipping address", md: 6 },
          ],
        },
        {
          key: "contact",
          title: "Contact",
          divider: true,
          fields: [
            { key: "contact_name", label: "Contact name", md: 4 },
            { key: "contact_phone", label: "Contact phone", md: 4 },
            { key: "contact_email", label: "Contact email", md: 4 },
          ],
        },
        {
          key: "dates",
          title: "Dates",
          divider: true,
          fields: [
            { key: "document_date_from", label: "Document date from", type: "date" },
            { key: "document_date_to", label: "Document date to", type: "date" },
            { key: "delivery_date_from", label: "Delivery date from", type: "date" },
            { key: "delivery_date_to", label: "Delivery date to", type: "date" },
            { key: "issued_at_from", label: "Issued at from", type: "date" },
            { key: "issued_at_to", label: "Issued at to", type: "date" },
            { key: "cancelled_at_from", label: "Cancelled at from", type: "date" },
            { key: "cancelled_at_to", label: "Cancelled at to", type: "date" },
            { key: "pdf_generated_at_from", label: "PDF generated at from", type: "date" },
            { key: "pdf_generated_at_to", label: "PDF generated at to", type: "date" },
            { key: "created_at_from", label: "Created at from", type: "date" },
            { key: "created_at_to", label: "Created at to", type: "date" },
            { key: "updated_at_from", label: "Updated at from", type: "date" },
            { key: "updated_at_to", label: "Updated at to", type: "date" },
          ],
        },
        {
          key: "details",
          title: "Details",
          divider: true,
          fields: [
            { key: "total_weight_min", label: "Minimum weight", type: "number", suffix: "kg" },
            { key: "total_weight_max", label: "Maximum weight", type: "number", suffix: "kg" },
            { key: "items_count_min", label: "Minimum rows", type: "number" },
            { key: "items_count_max", label: "Maximum rows", type: "number" },
            { key: "locale", label: "Language" },
            { key: "currency_code", label: "Currency" },
            { key: "has_pdf", label: "PDF status", type: "select", items: this.hasPdfItems },
            { key: "created_by", label: "Created by user ID", type: "number" },
            { key: "issued_by", label: "Issued by user ID", type: "number" },
            { key: "cancelled_by", label: "Cancelled by user ID", type: "number" },
            { key: "comment", label: "Comment contains", md: 6 },
            { key: "internal_note", label: "Internal note contains", md: 6 },
          ],
        },
      ];
    },
  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.fetchDeliveryNotes();
      },
    },
  },

  mounted() {
    this.fetchDeliveryNotes();
  },

  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },

  methods: {
    async fetchDeliveryNotes() {
      this.loading = true;

      try {
        const query = this.buildQuery();
        const { data } = await ApiService.get(ENDPOINT, "?" + query);

        this.deliveryNotes = data.data || [];
        this.total = data.total || 0;
      } catch (error) {
        this.showSnackbar(
          error?.response?.data?.message || "Failed to load delivery notes.",
          "error",
          5000
        );

        console.error("Delivery notes load error", error);
      } finally {
        this.loading = false;
      }
    },

    buildQuery() {
      const params = new URLSearchParams();

      params.append("page", this.options.page || 1);
      params.append("per_page", this.options.itemsPerPage || 15);

      if (this.search) {
        params.append("q", this.search);
      }

      if (this.options.sortBy && this.options.sortBy.length) {
        params.append("sort_by", this.options.sortBy[0]);
        params.append(
          "sort_direction",
          this.options.sortDesc && this.options.sortDesc[0] ? "desc" : "asc"
        );
      }

      Object.keys(this.filters).forEach((key) => {
        const value = this.filters[key];

        if (value !== null && value !== undefined && String(value).trim() !== "") {
          params.append(key, value);
        }
      });

      return params.toString();
    },

    reloadFirstPage() {
      if (this.options.page !== 1) {
        this.options.page = 1;
        return;
      }

      this.fetchDeliveryNotes();
    },

    applyFilters() {
      this.reloadFirstPage();
    },

    resetFilters() {
      this.filters = DEFAULT_FILTERS();
      this.search = "";
      this.reloadFirstPage();
    },

    handleSearchInput() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = setTimeout(() => {
        this.reloadFirstPage();
      }, 400);
    },

    clearSearch() {
      this.search = "";
      this.reloadFirstPage();
    },

    toggleFilters() {
      this.filterPanel = this.filterPanel === 0 ? null : 0;
    },

    openDetails(item) {
      if (!item || !item.id || !this.$refs.deliveryNoteDialog) {
        return;
      }

      this.$refs.deliveryNoteDialog.openExisting(item.id);
    },

    openPdf(item) {
      if (!item || !item.pdf_path) {
        this.showSnackbar("PDF file is not available.", "error", 5000);
        return;
      }

      const baseUrl = (process.env.VUE_APP_BACKEND_URL || "").replace(/\/$/, "");
      const url = baseUrl + "/public/" + item.pdf_path;

      window.open(url, "_blank");
    },

    formatDate(value) {
      if (!value) {
        return "-";
      }

      return String(value).substring(0, 10);
    },

    formatDateTime(value) {
      if (!value) {
        return "-";
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return String(value).substring(0, 16);
      }

      return date.toLocaleString();
    },

    formatWeight(value) {
      const weight = Number(value || 0);

      if (!weight) {
        return "-";
      }

      return weight.toFixed(2) + " kg";
    },

    formatShippingAddress(item) {
      const parts = [
        item.shipping_country,
        item.shipping_zip,
        item.shipping_city,
        item.shipping_address,
      ].filter(Boolean);

      return parts.length ? parts.join(" ") : "-";
    },

    formatStatus(status) {
      if (!status) {
        return "-";
      }

      return status.charAt(0).toUpperCase() + status.slice(1);
    },

    resolveStatusColor(status) {
      switch (status) {
        case "issued":
          return "success";
        case "draft":
          return "grey";
        case "planned":
          return "orange";
        case "cancelled":
          return "error";
        default:
          return "primary";
      }
    },

    showSnackbar(text, color = "success", timeout = 3000) {
      this.uSnackbar.text = text;
      this.uSnackbar.color = color;
      this.uSnackbar.timeout = timeout;
      this.uSnackbar.show = true;
    },
  },
};
</script>
<style scoped>
.v-expansion-panel-header{
    background-color: #ededed !important;
}
.v-expansion-panel-header--active{
    margin-bottom: 1em !important;
}
</style>